// Small hex-colour helpers shared by the site theme (ConfigContext) and the
// availability images (availabilityThemeUtil). Every function takes "#abc"
// or "#aabbcc"; callers validate with isValidHex first.

// Basic hex-color validation (#abc or #aabbcc) — used to guard the config's
// theme color fields before they're fed into a stylesheet, so a malformed
// value can't break the page's CSS.
const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/**
 * @param {unknown} value
 * @returns {boolean} whether value is a "#abc" / "#aabbcc" string
 */
export const isValidHex = (value) => typeof value === 'string' && HEX_COLOR_RE.test(value.trim());

/**
 * Expands "#abc" to "#aabbcc" and parses it.
 * @param {string} hex
 * @returns {{r: number, g: number, b: number}}
 */
export const hexToRgb = (hex) => {
  const clean = hex.trim().replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

/**
 * @param {{r: number, g: number, b: number}} rgb
 * @returns {string} "#aabbcc"
 */
const rgbToHex = ({ r, g, b }) => {
  const toHex = v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * @param {string} hex
 * @param {number} alpha - 0..1
 * @returns {string} "rgba(r, g, b, alpha)"
 */
export const hexToRgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * "r, g, b" — for CSS like rgba(var(--x-rgb), .4), so one variable serves
 * every transparency.
 * @param {string} hex
 * @returns {string}
 */
export const hexToRgbList = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return `${r}, ${g}, ${b}`;
};

/**
 * Lightens (positive percent) or darkens (negative percent) a hex color —
 * used to derive a hover/deep shade from a single configured accent color.
 * @param {string} hex
 * @param {number} percent - -100..100
 * @returns {string} "#aabbcc"
 */
export const shadeHex = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex);
  const amt = Math.round(2.55 * percent);
  return rgbToHex({ r: r + amt, g: g + amt, b: b + amt });
};

/**
 * Blends two colours.
 * @param {string} from
 * @param {string} to
 * @param {number} amount - 0 = from, 1 = to
 * @returns {string} "#aabbcc"
 */
export const mixHex = (from, to, amount) => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const t = Math.max(0, Math.min(1, amount));
  return rgbToHex({ r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t });
};

/**
 * WCAG relative luminance.
 * @param {string} hex
 * @returns {number} 0 (black) .. 1 (white)
 */
export const relativeLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * WCAG contrast ratio between two colours.
 * @param {string} a
 * @param {string} b
 * @returns {number} 1..21
 */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Moves `color` towards `towards` in small steps until it reaches `minRatio`
 * contrast against `background`, so a club's pale or dark accent still
 * reads on whatever it is printed on. Returns the colour unchanged when it
 * already passes.
 * @param {string} color
 * @param {string} background
 * @param {number} minRatio
 * @param {string} towards - usually '#ffffff' or '#000000'
 * @returns {string} "#aabbcc"
 */
export const ensureContrast = (color, background, minRatio, towards) => {
  let result = mixHex(color, color, 0);
  for (let step = 1; step <= 20 && contrastRatio(result, background) < minRatio; step += 1) {
    result = mixHex(color, towards, step * 0.05);
  }
  return result;
};

/**
 * A lighter or darker shade of the same colour, chosen to stand apart from it:
 * a dark colour gets a light tint (dark green -> light green), a light colour
 * gets a dark shade (light green -> dark green). Dark vs light is decided by
 * which of white or black reads better on it, so mid-tones go the way that
 * actually separates. The result is then pushed further the same way if
 * needed until it reaches `minContrast` against the original.
 * @param {string} hex - the base colour, e.g. a header background
 * @param {number} amount - 0..1, how far to mix toward white or black
 * @param {number} minContrast - minimum contrast ratio against `hex`
 * @returns {string} "#aabbcc"
 */
export const tonalCompanion = (hex, amount, minContrast) => {
  const isDark = contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#000000');
  const towards = isDark ? '#ffffff' : '#000000';
  return ensureContrast(mixHex(hex, towards, amount), hex, minContrast, towards);
};
